import * as React from 'react';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router-dom';
import { ReadOnlyDataGrid, ReadOnlyField, RowAction } from '../ReadOnlyDataGrid';
import { useAuth } from '../../contexts/AuthContext';

export interface DiagnosisTableRow {
  id: string;
  table_code_index?: string;
  customer_name?: string;
  site_address?: string;
  responsible_01?: string;
  last_update?: string;
  progress: number;
  _progressLoading?: boolean;
  /** Se false, la riga è apribile solo in visualizzazione */
  _canEdit?: boolean;
}

const DiagnosisProgressCell: React.FC<{ progress: number; loading: boolean }> = ({
  progress,
  loading,
}) => {
  if (progress >= 101) {
    return (
      <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 700 }}>
        CHIUSA
      </Typography>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', pt: 1 }}>
        <CircularProgress size={16} />
      </Box>
    );
  }

  return (
    <Stack spacing={0.1} sx={{ width: '100%' }}>
      <Typography variant="caption" color="text.secondary">
        {progress}%
      </Typography>
      <LinearProgress variant="determinate" value={progress} />
    </Stack>
  );
};

const fields: ReadOnlyField[] = [
  { field: 'table_code_index', headerName: 'Codice', width: 150 },
  { field: 'customer_name', headerName: 'Cliente', width: 240 },
  { field: 'site_address', headerName: 'Sito', width: 260 },
  { field: 'responsible_01', headerName: 'Responsabile', width: 220 },
  {
    field: 'last_update',
    headerName: 'Ultimo aggiornamento',
    width: 200,
    type: 'dateTime',
  },
  {
    field: 'progress',
    headerName: 'Avanzamento',
    width: 180,
    type: 'number',
    renderCell: (params: any) => (
      <DiagnosisProgressCell
        progress={params.row.progress as number}
        loading={Boolean(params.row._progressLoading)}
      />
    ),
  },
];

export const DIAGNOSIS_TABLE_ROW_HEIGHT_PX = 36;
export const DIAGNOSIS_TABLE_COLUMN_HEADER_HEIGHT_PX = 36;
export const DIAGNOSIS_TABLE_TOOLBAR_APPROX_PX = 52;
export const DIAGNOSIS_TABLE_FOOTER_APPROX_PX = 52;

export function getDiagnosisTableHeightForVisibleDataRows(
  dataRowCount: number,
  options: { isFiltrable?: boolean; showFooter?: boolean } = {}
): number {
  const { isFiltrable = true, showFooter = true } = options;
  let h =
    DIAGNOSIS_TABLE_COLUMN_HEADER_HEIGHT_PX + dataRowCount * DIAGNOSIS_TABLE_ROW_HEIGHT_PX;
  if (isFiltrable) h += DIAGNOSIS_TABLE_TOOLBAR_APPROX_PX;
  if (showFooter) h += DIAGNOSIS_TABLE_FOOTER_APPROX_PX;
  return h + 2;
}

const DEFAULT_FULL_PAGE_HEIGHT = 'calc(100vh - 185px)';

interface DiagnosisTableProps {
  rows: DiagnosisTableRow[];
  loading?: boolean;
  isFiltrable?: boolean;
  onRequestDelete?: (id: string) => void;
  height?: number | string;
  maxVisibleDataRows?: number;
  showFooter?: boolean;
}

export const DiagnosisTable: React.FC<DiagnosisTableProps> = ({
  rows,
  loading = false,
  isFiltrable = true,
  onRequestDelete,
  height = DEFAULT_FULL_PAGE_HEIGHT,
  maxVisibleDataRows,
  showFooter = true,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdminLike = user?.role === 'admin' || user?.role === 'mantainer';

  const getActionsForRow = React.useCallback(
    (row: DiagnosisTableRow): RowAction[] => {
      const canEditRow = row._canEdit !== false;
      const output: RowAction[] = [
        {
          label: canEditRow ? 'Apri' : 'Visualizza',
          icon: canEditRow ? <EditIcon /> : <VisibilityIcon />,
          onClick: id => navigate(`/diagnosis/${id}`),
        },
      ];
      if (canEditRow) {
        output.push({
          label: 'Chiudi ed esporta',
          icon: <FactCheckOutlinedIcon />,
          onClick: id => navigate(`/diagnosis/${id}?step=close-export`),
        });
      }
      if (isAdminLike && onRequestDelete) {
        output.push({
          label: 'Elimina',
          icon: <DeleteOutlineIcon />,
          onClick: id => onRequestDelete(String(id)),
        });
      }
      return output;
    },
    [navigate, isAdminLike, onRequestDelete]
  );

  const resolvedHeight =
    maxVisibleDataRows != null
      ? getDiagnosisTableHeightForVisibleDataRows(maxVisibleDataRows, {
          isFiltrable,
          showFooter,
        })
      : height;

  return (
    <ReadOnlyDataGrid
      fields={fields}
      data={rows}
      getRowActions={getActionsForRow}
      loading={loading}
      isFiltrable={isFiltrable}
      showFooter={showFooter}
      height={resolvedHeight}
    />
  );
};
